'use client';

import { c as _c } from "react/compiler-runtime";
import { type CSSProperties, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { SPARTAN_SLIDE_PAGINATION_LABELS } from '@/app/lcv/components/sections/spartan/shared/slide-pagination/constants';
import { SpartanSlidePaginationButtonStyled, SpartanSlidePaginationProgressStyled, SpartanSlidePaginationTrackStyled } from '@/app/lcv/components/sections/spartan/shared/slide-pagination/styles';
import { SPARTAN_SLIDE_PAGINATION_TRACKING } from '@/app/lcv/components/sections/spartan/shared/slide-pagination/tracking';
import type { SpartanSlidePaginationProps } from '@/app/lcv/components/sections/spartan/shared/slide-pagination/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
export const SpartanSlidePagination = t0 => {
  const $ = _c(35);
  const {
    testId: t1,
    itemTestIdPrefix,
    wrapperClassName,
    className,
    theme: t2,
    currentPage: t3,
    totalPages: t4,
    progress: t5,
    onPageChange,
    tracking,
    ref
  } = t0;
  const testId = t1 === undefined ? SPARTAN_SLIDE_PAGINATION_TRACKING.root : t1;
  const theme = t2 === undefined ? "light" : t2;
  const currentPage = t3 === undefined ? 1 : t3;
  const totalPages = t4 === undefined ? 1 : t4;
  const progress = t5 === undefined ? 1 : t5;
  const rootRef = useRef(null);
  const progressScale = Math.min(1, Math.max(0, progress));
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value => {
      const nextProgress = Math.min(1, Math.max(0, value));
      rootRef.current?.style.setProperty("--slide-pagination-progress", String(nextProgress));
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const updateProgress = t6;
  let t7;
  let t8;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => ({
      updateProgress
    });
    t8 = [updateProgress];
    $[1] = t7;
    $[2] = t8;
  } else {
    t7 = $[1];
    t8 = $[2];
  }
  useImperativeHandle(ref, t7, t8);
  let t10;
  let t9;
  if ($[3] !== progressScale) {
    t9 = () => {
      updateProgress(progressScale);
    };
    t10 = [progressScale, updateProgress];
    $[3] = progressScale;
    $[4] = t10;
    $[5] = t9;
  } else {
    t10 = $[4];
    t9 = $[5];
  }
  useEffect(t9, t10);
  let t11;
  if ($[6] !== currentPage || $[7] !== onPageChange) {
    t11 = page => {
      if (page === currentPage) {
        return;
      }
      safeCall(onPageChange, page);
    };
    $[6] = currentPage;
    $[7] = onPageChange;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const handlePageChange = t11;
  let t12;
  if ($[9] !== wrapperClassName) {
    t12 = cn("flex items-center gap-x-6", wrapperClassName);
    $[9] = wrapperClassName;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== progressScale) {
    t13 = {
      "--slide-pagination-progress": progressScale
    };
    $[11] = progressScale;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const t14 = t13 as CSSProperties;
  let t15;
  if ($[13] !== testId) {
    t15 = createDataAttributes({
      test: testId
    });
    $[13] = testId;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== className || $[16] !== currentPage || $[17] !== handlePageChange || $[18] !== itemTestIdPrefix || $[19] !== theme || $[20] !== totalPages || $[21] !== tracking) {
    let t17;
    if ($[23] !== className || $[24] !== currentPage || $[25] !== handlePageChange || $[26] !== itemTestIdPrefix || $[27] !== theme || $[28] !== tracking) {
      t17 = (_, index) => {
        const page_0 = index + 1;
        const isActive = page_0 === currentPage;
        const pageLabel = SPARTAN_SLIDE_PAGINATION_LABELS.page(page_0);
        const trackingProps = tracking ? createDataGA({
          ...tracking,
          click: true,
          text: pageLabel
        }) : {};
        return <button type="button" key={page_0} className={SpartanSlidePaginationButtonStyled({
          isActive,
          theme
        })} aria-current={isActive ? "page" : undefined} aria-label={pageLabel} {...createDataAttributes({
          test: itemTestIdPrefix ? `${itemTestIdPrefix}-${page_0}` : SPARTAN_SLIDE_PAGINATION_TRACKING.item(page_0),
          active: isActive ? "true" : null
        })} onClick={() => handlePageChange(page_0)} {...trackingProps}><span aria-hidden="true" className={SpartanSlidePaginationTrackStyled({
            isActive,
            theme,
            className
          })}><span className={SpartanSlidePaginationProgressStyled({
              isActive,
              theme
            })} style={{
              transform: "scaleX(var(--slide-pagination-progress))",
              transformOrigin: "0% 50%"
            } as CSSProperties} /></span></button>;
      };
      $[23] = className;
      $[24] = currentPage;
      $[25] = handlePageChange;
      $[26] = itemTestIdPrefix;
      $[27] = theme;
      $[28] = tracking;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    t16 = Array.from({
      length: totalPages
    }, t17);
    $[15] = className;
    $[16] = currentPage;
    $[17] = handlePageChange;
    $[18] = itemTestIdPrefix;
    $[19] = theme;
    $[20] = totalPages;
    $[21] = tracking;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[30] !== t12 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16) {
    t17 = <nav ref={rootRef} aria-label={SPARTAN_SLIDE_PAGINATION_LABELS.ariaLabel} className={t12} style={t14} {...t15}>{t16}</nav>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
};